import {
  CascadiaCustomIcon,
  CascadiaSpacing,
  CascadiaStandardIcon,
  CascadiaTypographyStyle,
} from "@certn/cascadia-tokens";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { ReactNode, useContext, useRef } from "react";
import { AriaButtonProps, useButton } from "react-aria";

import { useCobrandingEnabled } from "../../../context/BrandProvider";
import { NavigationContext } from "../../../context/NavigationProvider";
import { Space, useLayout } from "../../../foundations/grid-layout/Layout";
import { Icon } from "../../../foundations/icons/Icon/Icon";
import { getIcon } from "../../../foundations/icons/Icon/utils";
import { Text } from "../../../foundations/typography/Text";
import { NewBadge } from "../../badge/NewBadge";
import { mq, useGetAllFocusShadows } from "../../utils";

export interface NavigationItemProps extends AriaButtonProps {
  title: string;
  badgeCount?: number;
  icon?: CascadiaStandardIcon | CascadiaCustomIcon;
  isActive?: boolean;
  style?: CascadiaTypographyStyle;
  betaBadge?: ReactNode;
  wrapTitle?: boolean;
}

const iconSize: CascadiaSpacing = "space-5";

export function NavigationItem(props: NavigationItemProps) {
  const { icon, title, badgeCount, isActive, onPress, betaBadge, wrapTitle } =
    props;
  const { isCollapsed } = useContext(NavigationContext);
  const ref = useRef<HTMLButtonElement>(null);
  const getAllFocusShadows = useGetAllFocusShadows();
  const { buttonProps } = useButton({ ...props, onPress }, ref);
  const { toggleMenu } = useLayout();
  const cobrandingEnabled = useCobrandingEnabled();

  const iconObject = icon ? getIcon("custom", icon) : undefined;
  const activeIconColor = cobrandingEnabled
    ? "icon-base"
    : "icon-accent-tofino";

  return (
    <button
      data-testid={`sidebar-${slugify(title)}-button`}
      className={css(
        styles.container,
        css({
          "&:hover": {
            backgroundColor: "var(--background-ghost-hover)",
          },
          "&:focus-within": getAllFocusShadows(),
        }),
        isActive && styles.active
      )}
      data-collapsed={isCollapsed}
      data-wrap-title={wrapTitle || undefined}
      {...buttonProps}
      onClick={(e) => {
        toggleMenu(false);
        buttonProps.onClick?.(e);
      }}
      ref={ref}
    >
      <div
        data-collapsed={isCollapsed}
        data-wrap-title={wrapTitle || undefined}
        className={styles.leftWrapper}
      >
        {icon && (
          <div
            // The <Icon> component adds space-1-5 padding to standard icons but strips padding from custom icons
            // But that breaks the alignment when mixing standard and custom icons in a menu. So we manually add the padding to custom icons here.
            className={
              iconObject?.lib === "custom" ? styles.customIconWrapper : ""
            }
          >
            <Icon
              className={styles.iconSize}
              name={icon}
              color={isActive ? activeIconColor : "icon-weak"}
              padding="space-1-5"
            />
          </div>
        )}
        <div
          data-collapsed={isCollapsed}
          data-wrap-title={wrapTitle || undefined}
          data-testid="navigation-item-title-wrapper"
          className={styles.titleWrapper}
        >
          <Text style={props.style ?? "summary/nav-list"} color={"text-base"}>
            {title}
          </Text>
        </div>
      </div>
      <Space />
      {betaBadge && (
        <div className={css(styles.trailingWrapper)}>{betaBadge}</div>
      )}
      {badgeCount && (
        <div className={css(styles.trailingWrapper)}>
          <NewBadge size="large" type="subtle">
            {badgeCount}
          </NewBadge>
        </div>
      )}
    </button>
  );
}

const styles = {
  container: css({
    all: "unset",
    cursor: "pointer",
    display: "flex",
    boxSizing: "border-box",
    gap: "var(--space-2)",
    alignItems: "center",
    justifyItems: "start",
    width: "100%",
    borderRadius: "var(--border-radius-3)",
    minHeight: "40px",
    paddingTop: "var(--space-2)",
    paddingBottom: "var(--space-2)",
    paddingLeft: "var(--space-2)",
    paddingRight: "var(--space-2)",
    maxWidth: "100%",
    transition: "all 200ms ease",

    "&[data-collapsed=false]": mq({
      height: "auto",
      overflow: "visible",
      whiteSpace: ["normal", "normal", "nowrap"],
      width: ["100%", "100%", "max-content"],
      minWidth: "100%",
      maxWidth: ["100%", "100%", "none"],
    }),

    "&[data-collapsed=false][data-wrap-title=true]": {
      width: 0,
      minWidth: "100%",
      maxWidth: "100%",
      whiteSpace: "normal",
      height: "auto",
    },

    "&[data-collapsed=true]": {
      height: "40px",
      minHeight: "unset",
      paddingTop: "unset",
      paddingBottom: "unset",
      maxWidth: "40px",
      paddingLeft: "var(--space-1)",
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
  }),
  leftWrapper: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flex: "1 1 auto",
    minWidth: 0,
    "&[data-collapsed=false]": mq({
      overflow: "visible",
      flex: ["1 1 auto", "1 1 auto", "0 0 auto"],
      minWidth: [0, 0, "auto"],
    }),
    "&[data-collapsed=false][data-wrap-title=true]": {
      flex: "1 1 auto",
      minWidth: 0,
      overflow: "visible",
    },
    "&[data-collapsed=true]": {
      flex: "0 0 auto",
      minWidth: "auto",
      overflow: "visible",
      alignItems: "center",
    },
  }),
  titleWrapper: css({
    flex: "1 1 auto",
    minWidth: 0,
    transition: "opacity 200ms ease",
    whiteSpace: "normal",
    overflowWrap: "normal",
    "&[data-collapsed=false]": mq({
      whiteSpace: ["normal", "normal", "nowrap"],
      flex: ["1 1 auto", "1 1 auto", "0 0 auto"],
      minWidth: [0, 0, "auto"],
    }),
    "&[data-collapsed=false][data-wrap-title=true]": {
      whiteSpace: "normal",
      overflowWrap: "break-word",
      flex: "1 1 auto",
      minWidth: 0,
    },
    "&[data-collapsed=true]": {
      opacity: 0,
      flex: "0 0 auto",
      maxWidth: 0,
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
  }),
  trailingWrapper: css({
    display: "flex",
    alignItems: "center",
    flex: "0 0 auto",
  }),
  iconSize: css({
    width: `var(--${iconSize})`,
    height: `var(--${iconSize})`,
  }),
  customIconWrapper: css({
    padding: "var(--space-1-5)",
  }),
  active: css({ backgroundColor: "var(--background-accent-neutral-weak)" }),
};
